import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { JudgeRegistration } from '../../types/judging';
import { SCORE_CATEGORIES } from '../../types/judging';
import {
  allFinalizedInClass,
  buildScorecardReturnQuery,
  filterAndSortRegistrations,
  findNextInQueue,
  findNextUnscored,
  findPrevInQueue,
  loadJudgeQueue,
  parseJudgeListQuery,
  storeJudgeQueue,
  vehicleLabel,
} from '../../utils/judgeRegistrations';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import PublicBuildExplorer from '../PublicBuildExplorer';
import type { TreeModification } from '../ModificationTree';
import PageLoader from '../ui/PageLoader';
import Button from '../ui/Button';

type ScoreTab = 'score' | 'build' | 'photos';

type BuildSheet = {
  modifications: TreeModification[];
  images: Array<{ image_url?: string; caption?: string | null }>;
  description?: string;
  engine?: string;
  transmission?: string;
};

const EMPTY_SCORES: Record<string, string> = {};

const JudgeScorecard: React.FC = () => {
  const { eventId, registrationId } = useParams<{ eventId: string; registrationId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const regId = Number(registrationId);
  const evtId = Number(eventId);

  const listQuery = parseJudgeListQuery(searchParams.toString());
  const returnQs = buildScorecardReturnQuery(listQuery);
  const listHref = `/judge/events/${evtId}${returnQs}`;

  const [queue, setQueue] = useState<JudgeRegistration[]>([]);
  const [registration, setRegistration] = useState<JudgeRegistration | null>(null);
  const [scores, setScores] = useState<Record<string, string>>(EMPTY_SCORES);
  const [notes, setNotes] = useState('');
  const [finalized, setFinalized] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState({ scores: EMPTY_SCORES, notes: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [tab, setTab] = useState<ScoreTab>('score');
  const [buildSheet, setBuildSheet] = useState<BuildSheet | null>(null);
  const [buildLoading, setBuildLoading] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [endOfClass, setEndOfClass] = useState(false);
  const dirtyRef = useRef(false);

  const isDirty = useMemo(() => {
    if (finalized) return false;
    const scoreKeys = Array.from(
      new Set([...Object.keys(scores), ...Object.keys(savedSnapshot.scores)])
    );
    for (const key of scoreKeys) {
      if ((scores[key] ?? '') !== (savedSnapshot.scores[key] ?? '')) return true;
    }
    return notes !== savedSnapshot.notes;
  }, [scores, notes, savedSnapshot, finalized]);

  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  useEffect(() => {
    if (!regId || !evtId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setSavedMsg(null);
        setEndOfClass(false);
        setTab('score');
        setBuildSheet(null);
        setBuildError(null);

        const [dashboard, existing] = await Promise.all([
          apiService.getJudgeRegistrations(evtId),
          apiService.getJudgeScore(regId),
        ]);

        if (cancelled) return;

        const filtered = filterAndSortRegistrations(dashboard.registrations, {
          classId: listQuery.classId,
          status: listQuery.status,
          search: '',
          sort: 'class',
        });

        const storedIds = loadJudgeQueue(evtId);
        let ordered = filtered;
        if (storedIds && storedIds.length > 0) {
          const byId = new Map(dashboard.registrations.map((r) => [r.id, r]));
          const fromStore = storedIds
            .map((qid) => byId.get(qid))
            .filter((r): r is JudgeRegistration => !!r)
            .filter((r) =>
              filterAndSortRegistrations([r], {
                classId: listQuery.classId,
                status: listQuery.status,
                search: '',
                sort: 'class',
              }).length > 0
            );
          if (fromStore.length > 0) ordered = fromStore;
        }
        storeJudgeQueue(
          evtId,
          ordered.map((r) => r.id)
        );
        setQueue(ordered);

        const reg =
          dashboard.registrations.find((r) => r.id === regId) ||
          ordered.find((r) => r.id === regId) ||
          null;
        setRegistration(reg);

        const s: Record<string, string> = {};
        if (existing) {
          const scoreRow = existing as unknown as Record<string, number | null | undefined>;
          SCORE_CATEGORIES.forEach(({ key }) => {
            const val = scoreRow[`score_${key}`];
            if (val !== null && val !== undefined) s[key] = String(val);
          });
          setScores(s);
          setNotes(existing.notes || '');
          setFinalized(existing.is_finalized);
          setSavedSnapshot({ scores: { ...s }, notes: existing.notes || '' });
        } else {
          setScores({});
          setNotes('');
          setFinalized(false);
          setSavedSnapshot({ scores: {}, notes: '' });
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when route ids or filter query change
  }, [regId, evtId, listQuery.classId, listQuery.status]);

  useEffect(() => {
    if (tab !== 'build' && tab !== 'photos') return;
    if (buildSheet || buildLoading) return;
    if (!registration?.vehicle.public_slug || !registration.owner_username) {
      setBuildError('This build is not public or has no share link.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setBuildLoading(true);
        setBuildError(null);
        const data = await apiService.getPublicVehicle(
          registration.owner_username,
          registration.vehicle.public_slug!
        );
        if (cancelled) return;
        setBuildSheet({
          modifications: (data.modifications || []) as TreeModification[],
          images: data.images || [],
          description: data.vehicle.description,
          engine: data.vehicle.engine,
          transmission: data.vehicle.transmission,
        });
      } catch (err: any) {
        if (!cancelled) setBuildError(err.message || 'Could not load build sheet');
      } finally {
        if (!cancelled) setBuildLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, registration, buildSheet, buildLoading]);

  const confirmLeaveIfDirty = () => {
    if (!dirtyRef.current) return true;
    return window.confirm('You have unsaved changes. Leave without saving?');
  };

  const navigateToReg = (next: JudgeRegistration | null) => {
    if (!next) {
      setEndOfClass(true);
      return;
    }
    navigate(`/judge/events/${evtId}/score/${next.id}${returnQs}`);
  };

  const allCriteriaFilled = SCORE_CATEGORIES.every(({ key }) => {
    const v = scores[key];
    return v !== undefined && v !== '' && !Number.isNaN(Number(v));
  });

  const handleSave = async (finalize: boolean, thenContinue: boolean) => {
    if (finalize) {
      if (!allCriteriaFilled) {
        setError('All five criteria are required to finalize.');
        setTab('score');
        return;
      }
      if (
        !window.confirm(
          'Finalize this score? You will not be able to edit it afterward.'
        )
      ) {
        return;
      }
    }
    try {
      setSaving(true);
      setError(null);
      setSavedMsg(null);
      const payload: Record<string, unknown> = {
        registration_id: regId,
        notes,
        is_finalized: finalize,
      };
      SCORE_CATEGORIES.forEach(({ key }) => {
        payload[`score_${key}`] =
          scores[key] !== undefined && scores[key] !== '' ? Number(scores[key]) : null;
      });
      const result = await apiService.saveJudgeScore(payload as any);
      setFinalized(result.is_finalized);
      setSavedSnapshot({ scores: { ...scores }, notes });
      dirtyRef.current = false;

      void apiService.trackEvent(
        finalize ? 'judge_score_submitted' : 'judge_score_draft_saved',
        { event_id: evtId, metadata: { registration_id: regId } }
      );

      // Update local queue status for next-unscored
      setQueue((prev) =>
        prev.map((r) =>
          r.id === regId
            ? {
                ...r,
                my_score_status: result.is_finalized ? 'finalized' : 'draft',
                my_score_id: result.id,
              }
            : r
        )
      );

      setSavedMsg(result.is_finalized ? 'Score finalized' : 'Draft saved');

      if (thenContinue) {
        const updatedQueue = queue.map((r) =>
          r.id === regId
            ? {
                ...r,
                my_score_status: (result.is_finalized ? 'finalized' : 'draft') as JudgeRegistration['my_score_status'],
              }
            : r
        );
        const next = findNextUnscored(updatedQueue, regId);
        if (
          !next &&
          registration &&
          allFinalizedInClass(
            updatedQueue.length ? updatedQueue : [registration],
            listQuery.classId ?? registration.class_id
          )
        ) {
          void apiService.trackEvent('judge_class_completed', {
            event_id: evtId,
            metadata: {
              class_id: listQuery.classId ?? registration.class_id,
            },
          });
          setEndOfClass(true);
          return;
        }
        navigateToReg(next);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const bumpScore = (key: string, delta: number) => {
    if (finalized) return;
    setScores((prev) => {
      const cur = prev[key] !== undefined && prev[key] !== '' ? Number(prev[key]) : 0;
      const next = Math.min(10, Math.max(0, Math.round((cur + delta) * 2) / 2));
      return { ...prev, [key]: String(next) };
    });
  };

  const inputClass =
    'w-full bg-[#0a0a0a] border-2 border-white/[0.12] rounded-xl px-4 py-3.5 text-white text-xl font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/[0.35] disabled:opacity-50 min-h-[52px] text-center';

  if (loading) return <PageLoader message="Loading scorecard…" />;

  if (error && !registration) {
    return (
      <div className="py-12 flex flex-col items-center justify-center px-4">
        <p className="text-red-400 mb-4 text-center max-w-sm">{error}</p>
        <Link to={listHref} className="text-gray-300 hover:text-white text-sm min-h-[44px] inline-flex items-center">
          ← Back to vehicles
        </Link>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="py-8">
        <p className="text-red-400">This vehicle is not in your judging list.</p>
        <Link to={listHref} className="text-gray-300 mt-4 inline-block min-h-[44px]">
          ← Back to vehicles
        </Link>
      </div>
    );
  }

  const label = vehicleLabel(registration);
  const prev = findPrevInQueue(queue, regId);
  const next = findNextInQueue(queue, regId);
  const nextUnscored = findNextUnscored(queue, regId);
  const queueIndex = queue.findIndex((r) => r.id === regId);

  return (
    <div className="flex flex-col pb-36 max-w-lg mx-auto">
      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-5 sm:py-6">
        <button
          type="button"
          onClick={() => {
            if (confirmLeaveIfDirty()) navigate(listHref);
          }}
          className="text-sm text-gray-500 hover:text-white mb-5 inline-flex items-center min-h-[44px]"
        >
          ← All vehicles
        </button>

        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <p className="kicker mb-2">Scoring</p>
            <h1 className="heading-section text-2xl mb-1 truncate">{label}</h1>
            <p className="text-sm text-muted">
              {registration.class_name || 'Open class'} ·{' '}
              {registration.owner_display_name || `@${registration.owner_username}`}
            </p>
          </div>
          {queueIndex >= 0 && (
            <p className="text-meta tabular-nums shrink-0 pt-6">
              {queueIndex + 1}/{queue.length}
            </p>
          )}
        </div>

        <div className="flex gap-1 mb-4 border-b border-white/[0.06]">
          {([
            ['score', 'Score'],
            ['build', 'Build'],
            ['photos', 'Photos'],
          ] as [ScoreTab, string][]).map(([id, name]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`flex-1 px-3 py-3 text-sm font-medium border-b-2 -mb-px min-h-[44px] ${
                tab === id ? 'border-white text-white' : 'border-transparent text-gray-500'
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        <div className="flex gap-2 mb-5">
          <Button
            variant="secondary"
            size="sm"
            disabled={!prev}
            className="flex-1 min-h-[40px]"
            onClick={() => {
              if (prev && confirmLeaveIfDirty()) navigateToReg(prev);
            }}
          >
            ← Prev
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!nextUnscored}
            className="flex-1 min-h-[40px]"
            onClick={() => {
              if (nextUnscored && confirmLeaveIfDirty()) navigateToReg(nextUnscored);
            }}
          >
            Next unscored
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!next}
            className="flex-1 min-h-[40px]"
            onClick={() => {
              if (next && confirmLeaveIfDirty()) navigateToReg(next);
            }}
          >
            Next →
          </Button>
        </div>

        {endOfClass && (
          <div className="mb-5 rounded-xl border border-emerald-800/50 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-200">
            You&apos;ve finished this class filter.
            <div className="mt-2">
              <Link to={listHref} className="text-emerald-300 underline underline-offset-2">
                Back to vehicle list
              </Link>
            </div>
          </div>
        )}

        {error && (
          <div className="text-red-400 text-sm mb-4 rounded-lg border border-red-900/50 bg-red-950/30 px-3 py-2">
            {error}
          </div>
        )}
        {savedMsg && !error && (
          <div className="text-emerald-400 text-sm mb-4" role="status">
            {savedMsg}
          </div>
        )}
        {finalized && (
          <div className="text-amber-200/90 text-sm mb-4 font-medium">Finalized (locked)</div>
        )}
        {isDirty && !finalized && (
          <div className="text-amber-400/80 text-xs mb-3">Unsaved changes</div>
        )}

        {tab === 'score' && (
          <>
            {(registration.vehicle.engine || registration.vehicle.transmission) && (
              <div className="border border-white/[0.06] rounded-xl px-4 py-3 mb-6 text-sm text-gray-400 space-y-1">
                {registration.vehicle.engine && <p>{registration.vehicle.engine}</p>}
                {registration.vehicle.transmission && <p>{registration.vehicle.transmission}</p>}
              </div>
            )}

            <div className="space-y-5 mb-6">
              {SCORE_CATEGORIES.map(({ key, label: catLabel }) => (
                <div key={key}>
                  <label
                    htmlFor={`score-${key}`}
                    className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2"
                  >
                    {catLabel}
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={finalized}
                      onClick={() => bumpScore(key, -0.5)}
                      className="w-12 h-12 rounded-xl border border-white/[0.10] text-xl text-white disabled:opacity-40 shrink-0"
                      aria-label={`Decrease ${catLabel}`}
                    >
                      −
                    </button>
                    <input
                      id={`score-${key}`}
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={10}
                      step={0.5}
                      value={scores[key] ?? ''}
                      disabled={finalized}
                      onChange={(e) => setScores((s) => ({ ...s, [key]: e.target.value }))}
                      className={inputClass}
                      placeholder="0-10"
                    />
                    <button
                      type="button"
                      disabled={finalized}
                      onClick={() => bumpScore(key, 0.5)}
                      className="w-12 h-12 rounded-xl border border-white/[0.10] text-xl text-white disabled:opacity-40 shrink-0"
                      aria-label={`Increase ${catLabel}`}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-6">
              <label
                htmlFor="judge-notes"
                className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2"
              >
                Notes
              </label>
              <textarea
                id="judge-notes"
                value={notes}
                disabled={finalized}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-[#0a0a0a] border-2 border-white/[0.12] rounded-xl px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-white/40 disabled:opacity-50 min-h-[88px]"
                rows={3}
                placeholder="Optional judging notes"
              />
            </div>
          </>
        )}

        {tab === 'build' && (
          <div className="mb-6 space-y-4">
            {buildLoading && <p className="text-sm text-gray-500">Loading build sheet…</p>}
            {buildError && <p className="text-sm text-gray-500">{buildError}</p>}
            {buildSheet && (
              <>
                {(buildSheet.engine || buildSheet.transmission || buildSheet.description) && (
                  <div className="rounded-xl border border-white/[0.06] px-4 py-3 text-sm text-gray-400 space-y-2">
                    {buildSheet.engine && <p>Engine: {buildSheet.engine}</p>}
                    {buildSheet.transmission && <p>Transmission: {buildSheet.transmission}</p>}
                    {buildSheet.description && (
                      <p className="text-gray-300 whitespace-pre-wrap">{buildSheet.description}</p>
                    )}
                  </div>
                )}
                <PublicBuildExplorer
                  modifications={buildSheet.modifications}
                  compact
                  syncUrl={false}
                  defaultCollapseLarge={false}
                />
              </>
            )}
          </div>
        )}

        {tab === 'photos' && (
          <div className="mb-6">
            {buildLoading && <p className="text-sm text-gray-500">Loading photos…</p>}
            {buildError && !buildLoading && (
              <p className="text-sm text-gray-500">{buildError}</p>
            )}
            {buildSheet && buildSheet.images.length === 0 && (
              <p className="text-sm text-gray-500">No public photos available.</p>
            )}
            {buildSheet && buildSheet.images.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {buildSheet.images.map((img, i) => {
                  const src = resolvePublicFileUrl(img.image_url) || img.image_url;
                  if (!src) return null;
                  return (
                    <a
                      key={i}
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square rounded-lg overflow-hidden border border-white/[0.06] bg-[#0a0a0a]"
                    >
                      <img src={src} alt={img.caption || ''} className="w-full h-full object-cover" />
                    </a>
                  );
                })}
              </div>
            )}
            {!registration.vehicle.public_slug && !buildLoading && (
              <p className="text-sm text-gray-500">
                No public profile for this vehicle. Photos unavailable.
              </p>
            )}
          </div>
        )}
      </div>

      {!finalized && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-white/[0.06] bg-[#0a0a0a]/95 backdrop-blur-md px-4 py-4 safe-area-pb">
          <div className="max-w-lg mx-auto flex flex-col gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleSave(true, true)}
              className="btn-primary w-full min-h-[48px] text-base"
            >
              {saving ? 'Saving…' : 'Finalize score'}
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => handleSave(false, false)}
                className="btn-secondary flex-1 min-h-[44px]"
              >
                Save draft
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => handleSave(false, true)}
                className="btn-secondary flex-1 min-h-[44px]"
              >
                Save & continue
              </button>
            </div>
          </div>
        </div>
      )}

      {finalized && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-white/[0.06] bg-[#0a0a0a]/95 backdrop-blur-md px-4 py-4 safe-area-pb">
          <div className="max-w-lg mx-auto">
            <button
              type="button"
              className="btn-primary w-full min-h-[48px]"
              disabled={!nextUnscored}
              onClick={() => navigateToReg(nextUnscored)}
            >
              {nextUnscored ? 'Next unscored →' : 'Class complete'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JudgeScorecard;
